import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiDollarSign, FiUser, FiCheckCircle, FiFileText, FiClock, FiPlusCircle, FiX, FiExternalLink, FiSearch, FiMaximize2, FiCamera, FiBox, FiTrash2, FiCalendar, FiEye, FiPlus, FiPrinter, FiInbox, FiCheck, FiEdit2 } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import jsQR from 'jsqr';
import { paymentService, patientService, inventoryService } from '../../services/api';
import { toast } from 'react-toastify';

const QRScannerModal = ({ onClose, onScan }) => {
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  useEffect(() => {
    let isUnmounted = false;
    let animationId;
    let stream;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (isUnmounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        video.srcObject = stream;
        video.setAttribute("playsinline", true);
        
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
        requestAnimationFrame(tick);
      } catch (err) {
        if (!isUnmounted) {
          toast.error('Camera access denied or unavailable.');
          onClose();
        }
      }
    };

    const tick = () => {
      if (isUnmounted) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        if (canvas.height !== video.videoHeight || canvas.width !== video.videoWidth) {
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
        if (code && code.data) {
          if (!isUnmounted) onScan(code.data);
          return; 
        }
      }
      animationId = requestAnimationFrame(tick);
    };

    startCamera();
    return () => {
      isUnmounted = true;
      cancelAnimationFrame(animationId);
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (video) video.srcObject = null;
    };
  }, [onClose, onScan]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99] flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-3xl overflow-hidden shadow-2xl relative w-full max-w-sm border border-slate-700" onClick={e => e.stopPropagation()}>
         <div className="p-5 bg-slate-900 text-white text-center font-bold tracking-widest uppercase text-xs">
           Live Scanner Active
         </div>
         <div className="relative aspect-square bg-black overflow-hidden flex items-center justify-center">
           <video ref={videoRef} className="w-full h-full object-cover opacity-60" />
           <canvas ref={canvasRef} className="hidden" />
           <div className="absolute inset-10 border-2 border-green-500 rounded-2xl shadow-[0_0_0_400px_rgba(0,0,0,0.6)] animate-pulse"></div>
         </div>
         <div className="p-6 bg-slate-900 text-center border-t border-slate-700">
           <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mb-4">Position QR inside the frame</p>
           <button onClick={onClose} className="w-full py-4 bg-slate-800 text-white rounded-xl font-bold uppercase text-[11px] hover:bg-slate-700 transition-colors">
              Cancel Scan
           </button>
         </div>
      </div>
    </div>
  );
};

const Payments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  
  const [selectedItems, setSelectedItems] = useState([]);
  const [inventorySelect, setInventorySelect] = useState('');
  const [inventoryQty, setInventoryQty] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [volatileNextVisit, setVolatileNextVisit] = useState('');

  const [totalAmount, setTotalAmount] = useState('');
  const [paidToday, setPaidToday] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentPayments, setRecentPayments] = useState([]);
  
  // Manual Overrides for Invoice Specifications
  const [mFrame, setMFrame] = useState('');
  const [mLens, setMLens] = useState('');
  const [mAccessory, setMAccessory] = useState('');
  const [mDemographic, setMDemographic] = useState('unisex');
  const [mMaterial, setMMaterial] = useState('plastic');
  const [mMetalType, setMMetalType] = useState('full_frame');
  const [mSvType, setMSvType] = useState('minus');
  const [mBifocalType, setMBifocalType] = useState('normal');
  const [mBifocalShape, setMBifocalShape] = useState('round_top');
  
  // Manual Overrides
  const [showManualPatient, setShowManualPatient] = useState(false);
  const [customPatientName, setCustomPatientName] = useState('');
  const [showManualSpecs, setShowManualSpecs] = useState(false);
  const [showManualFrameSpec, setShowManualFrameSpec] = useState(false);
  const [showManualLensSpec, setShowManualLensSpec] = useState(false);
  
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [topUpModal, setTopUpModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDeliveryDate, setEditDeliveryDate] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [qrModal, setQrModal] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [showScanner, setShowScanner] = useState(false);

  const prefilledPatient = location.state?.patient;

  useEffect(() => {
    fetchPatients();
    fetchPayments();
    fetchInventory();
    if (prefilledPatient) {
      setSelectedPatientId(prefilledPatient.id);
    }
  }, [prefilledPatient]);

  // Handle auto-populating items when inventory and prefilled patient are ready
  useEffect(() => {
    if (prefilledPatient && inventory.length > 0 && selectedItems.length === 0) {
      const disp = prefilledPatient.dispensing_details;
      const autoItems = [];

      // 1. Add Frame
      if (disp.frame_id) {
        const frame = inventory.find(i => i.id.toString() === disp.frame_id.toString());
        if (frame) autoItems.push({ ...frame, qty: 1 });
      }

      // 2. Add Lens
      if (disp.lens_type && disp.lens_type !== 'none') {
        const lens = inventory.find(i => i.item_name === disp.lens_type);
        if (lens) autoItems.push({ ...lens, qty: 1 });
      }

      // 3. Add Accessory
      if (disp.accessory_id) {
        const acc = inventory.find(i => i.id.toString() === disp.accessory_id.toString());
        if (acc) autoItems.push({ ...acc, qty: 1 });
      }

      if (autoItems.length > 0) {
        setSelectedItems(autoItems.map(item => ({
          id: item.id,
          item_name: item.item_name,
          category: item.category,
          price: item.price,
          qty: 1
        })));
        setMFrame(disp.frame_brand || '');
        setMLens(disp.lens_type || '');
        setMAccessory(disp.accessory_name || '');
        setMDemographic(disp.frame_demographic || 'unisex');
        setMMaterial(disp.frame_material || 'plastic');
        setMMetalType(disp.frame_metal_type || 'full_frame');
        setMSvType(disp.cr39_sv_type || 'minus');
        setMBifocalType(disp.cr39_bifocal_type || 'normal');
        setMBifocalShape(disp.cr39_bifocal_shape || 'round_top');
        setInvoiceModal(true); 
      }
    }
  }, [inventory, prefilledPatient, selectedItems.length]);

  const handlePatientSelect = (pid) => {
    setSelectedPatientId(pid);
    const pat = patients.find(p => p.id.toString() === pid.toString());
    if (pat && pat.dispensing_details) {
      let disp = typeof pat.dispensing_details === 'string' ? JSON.parse(pat.dispensing_details) : pat.dispensing_details;
      setMFrame(disp.frame_brand || '');
      setMLens(disp.lens_type || '');
      setMAccessory(disp.accessory_name || '');
      setMDemographic(disp.frame_demographic || 'unisex');
      setMMaterial(disp.frame_material || 'plastic');
      setMMetalType(disp.frame_metal_type || 'full_frame');
      setMSvType(disp.cr39_sv_type || 'minus');
      setMBifocalType(disp.cr39_bifocal_type || 'normal');
      setMBifocalShape(disp.cr39_bifocal_shape || 'round_top');
    } else {
      setMFrame(''); setMLens(''); setMAccessory('');
      setMDemographic('unisex'); setMMaterial('plastic');
      setMMetalType('full_frame');
      setMSvType('minus'); setMBifocalType('normal'); setMBifocalShape('round_top');
    }
  };

  const toggleManualFrameSpec = () => {
    setShowManualFrameSpec(!showManualFrameSpec);
    setMFrame('');
  };

  const toggleManualLensSpec = () => {
    setShowManualLensSpec(!showManualLensSpec);
    setMLens('');
  };

  // Recalculate total when items change
  useEffect(() => {
    if (selectedItems.length > 0) {
      const sum = selectedItems.reduce((acc, curr) => acc + (parseFloat(curr.price) * curr.qty), 0);
      setTotalAmount(sum.toFixed(2));
    }
  }, [selectedItems]);

  const fetchPatients = async () => {
    try {
      const data = await patientService.getAll();
      setPatients(data);
    } catch (e) { toast.error('Failed to load patients.'); }
  };

  const fetchPayments = async () => {
    try {
      const data = await paymentService.getAll();
      setRecentPayments(data);
    } catch (e) { console.error(e); }
  };

  const fetchInventory = async () => {
    try {
      const data = await inventoryService.getAll();
      setInventory(data);
    } catch (e) { console.error(e); }
  };

  const filteredPayments = recentPayments.filter(p => 
    !searchQuery || 
    (p.invoice_no || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (p.patient_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const balance = totalAmount && paidToday
    ? Math.max(0, parseFloat(totalAmount || 0) - parseFloat(paidToday || 0)).toFixed(2)
    : '';

  const addItemToInvoice = () => {
    if (!inventorySelect) return;
    const itm = inventory.find(i => i.id.toString() === inventorySelect);
    if (itm) {
      const qty = parseInt(inventoryQty) || 1;
      const itemTotal = parseFloat(itm.price) * qty;
      
      setSelectedItems(prev => [...prev, {
        id: itm.id,
        item_name: itm.item_name,
        category: itm.category,
        price: itm.price,
        qty: qty
      }]);
      
      // Auto-calculate totalAmount
      setTotalAmount(prev => {
        const current = parseFloat(prev || 0);
        return (current + itemTotal).toFixed(2);
      });

      setInventorySelect('');
      setInventoryQty(1);
    }
  };

  const removeItem = (idx) => {
    const item = selectedItems[idx];
    if (item) {
      const itemTotal = parseFloat(item.price) * (item.qty || 1);
      setTotalAmount(prev => {
        const current = parseFloat(prev || 0);
        return Math.max(0, current - itemTotal).toFixed(2);
      });
    }
    setSelectedItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const isManual = showManualPatient;
    
    // Specific validations
    if (!isManual && !selectedPatientId) {
       toast.warning('Please select a patient from the list.');
       return;
    }
    if (isManual && !customPatientName) {
       toast.warning('Please enter the walk-in customer name.');
       return;
    }
    if (!totalAmount || parseFloat(totalAmount) < 0) {
       toast.warning('Please enter a valid Total Bill amount.');
       return;
    }

    setLoading(true);
    try {
      const payload = {
        patient_id: isManual ? null : selectedPatientId,
        custom_patient_name: isManual ? customPatientName : null,
        total_amount: parseFloat(totalAmount),
        paid_amount: parseFloat(paidToday || totalAmount),
        notes: notes,
        items: selectedItems.length > 0 ? selectedItems : null,
        delivery_date: deliveryDate
      };

      const resp = await paymentService.add(payload);

      const pat = isManual ? null : (patients.find(p => p.id === selectedPatientId) || prefilledPatient);
      let d_details = pat?.dispensing_details;
      if (typeof d_details === 'string') {
        try { d_details = JSON.parse(d_details); } catch(e) { d_details = null; }
      }
      
      // Merge with manual overrides
      const final_dispensing = {
        ...(d_details || {}),
        frame_brand: mFrame || d_details?.frame_brand || '',
        lens_type: mLens || d_details?.lens_type || '',
        accessory_name: mAccessory || d_details?.accessory_name || '',
        frame_demographic: mDemographic || d_details?.frame_demographic || 'unisex',
        frame_material: mMaterial || d_details?.frame_material || 'plastic',
        frame_metal_type: mMetalType || d_details?.frame_metal_type || 'full_frame',
        cr39_sv_type: mSvType || d_details?.cr39_sv_type || 'minus',
        cr39_bifocal_type: mBifocalType || d_details?.cr39_bifocal_type || 'normal',
        cr39_bifocal_shape: mBifocalShape || d_details?.cr39_bifocal_shape || 'round_top'
      };

      setPrintData({
        invoice_no: resp.invoice_no || `INV-${new Date().getTime()}`,
        patient_name: isManual ? customPatientName : (pat?.name || 'Unknown'),
        patient_contact: pat?.contact || '-',
        patient_reg_no: pat?.reg_no || '-',
        dispensing_details: final_dispensing,
        total: parseFloat(totalAmount),
        paid: parseFloat(paidToday || totalAmount),
        balance: resp.balance || 0,
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        items: selectedItems,
        delivery_date: deliveryDate,
        next_visit: volatileNextVisit
      });

      toast.success('Invoice created successfully!');
      setTotalAmount(''); setPaidToday(''); setNotes(''); setSelectedItems([]); setDeliveryDate(''); setVolatileNextVisit('');
      setCustomPatientName(''); setShowManualPatient(false); setShowManualSpecs(false);
      setInvoiceModal(false);
      fetchPayments();
      // Remove states to prevent looping
      navigate('/admin/payments', { replace: true, state: {} });

      setTimeout(() => {
        window.print();
        setTimeout(() => setPrintData(null), 1000); 
      }, 500);

    } catch (e) {
      toast.error(e.message || 'Failed to save invoice');
    } finally { setLoading(false); }
  };

  const fetchPaymentHistory = async (id) => {
    setLoadingHistory(true);
    try {
      const data = await paymentService.getHistory(id);
      setPaymentHistory(data);
    } catch (err) {
      toast.error('Failed to fetch payment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUpdateDetails = async (e) => {
    e.preventDefault();
    if (!editModal) return;
    setEditLoading(true);
    try {
      await paymentService.updateDetails({
        id: editModal.id,
        notes: editNotes,
        delivery_date: editDeliveryDate
      });
      toast.success('Invoice details updated!');
      setEditModal(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.message || 'Failed to update details');
    } finally {
      setEditLoading(false);
    }
  };

  const handleTopUp = async (e) => {
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      toast.warning('Enter a valid amount.');
      return;
    }
    setTopUpLoading(true);
    try {
      const resp = await paymentService.topUp({ id: topUpModal.id, top_up_amount: parseFloat(topUpAmount) });
      
      const pat = patients.find(p => p.id === topUpModal.patient_id);
      let d_details = pat?.dispensing_details;
      if (typeof d_details === 'string') {
        try { d_details = JSON.parse(d_details); } catch(e) { d_details = null; }
      } else if (topUpModal.dispensing_details) {
         // Backend join might have provided it
         d_details = typeof topUpModal.dispensing_details === 'string' ? JSON.parse(topUpModal.dispensing_details) : topUpModal.dispensing_details;
      }

      setPrintData({
        invoice_no: topUpModal.invoice_no,
        patient_name: pat?.name || topUpModal.patient_name || 'Unknown',
        patient_contact: pat?.contact || topUpModal.patient_contact || '-',
        patient_reg_no: pat?.reg_no || topUpModal.reg_no || '-',
        dispensing_details: d_details,
        total: parseFloat(topUpModal.total_amount),
        paid: parseFloat(resp.new_paid),
        balance: parseFloat(resp.new_balance),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        items: typeof topUpModal.items === 'string' ? JSON.parse(topUpModal.items) : topUpModal.items,
        delivery_date: topUpModal.delivery_date
      });

      toast.success('Balance payment recorded!');
      setTopUpModal(null); setTopUpAmount('');
      fetchPayments();

      setTimeout(() => {
        window.print();
        setTimeout(() => setPrintData(null), 1000);
      }, 500);

    } catch (e) {
      toast.error(e.message || 'Top-up failed');
    } finally { setTopUpLoading(false); }
  };

  const handleQRScanResult = (invoiceCode) => {
    setShowScanner(false);
    toast.success(`Scanned: ${invoiceCode}`);
    setSearchQuery(invoiceCode);

    const invoice = recentPayments.find(p => p.invoice_no === invoiceCode);
    if (invoice && parseFloat(invoice.balance) > 0) {
      setTopUpModal(invoice);
      setTopUpAmount('');
    } else if (invoice && parseFloat(invoice.balance) <= 0) {
      toast.info('This invoice is already fully paid.');
    }
  };

  const getPricingContext = () => {
    if (!selectedPatientId) return null;
    const pat = patients.find(p => p.id === selectedPatientId) || prefilledPatient;
    if (!pat) return null;
    let dispensing = pat.dispensing_details;
    if (typeof dispensing === 'string') {
      try { dispensing = JSON.parse(dispensing); } catch (e) { dispensing = null; }
    }
    if (!dispensing) return null;

    return (
      <div className="bg-syana-blue-50/50 p-4 rounded-xl border border-syana-blue-100 space-y-3">
        <div className="flex items-center space-x-2 border-b border-syana-blue-100 pb-2">
          <FiFileText className="text-syana-blue-600" />
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-syana-blue-800">Registration Context</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
          <div>
            <span className="text-slate-400 block text-[10px] font-bold uppercase tracking-widest">Lens</span>
            <span className="font-black text-slate-900 capitalize text-xs">{dispensing?.lens_type?.replace(/_/g, ' ') || '—'}</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[10px] font-bold uppercase tracking-widest">Frame</span>
            <span className="font-black text-slate-900 capitalize text-xs">
              {dispensing?.frame_material ? `${dispensing.frame_brand || 'Standard'} (${dispensing.frame_material})` : '—'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-slate-200 text-slate-600 flex items-center justify-center">
                <FiClock className="text-lg" />
              </div>
              <div>
                <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Ledger</h3>
                <h4 className="font-bold text-sm text-slate-900">Transaction Records</h4>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search / Scan QR..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm w-48 lg:w-64"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="w-9 h-9 flex items-center justify-center bg-slate-100 text-slate-600 rounded-xl hover:bg-syana-blue-50 hover:text-syana-blue-600 hover:border-syana-blue-200 transition-all border border-slate-200 shadow-sm"
                  title="Enable Webcam Scanner"
                >
                  <FiCamera size={15} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setInvoiceModal(true)}
                className="flex items-center space-x-2 px-5 py-2.5 bg-green-600 text-white rounded-xl text-[11px] font-bold uppercase tracking-widest shadow-md shadow-green-200 hover:bg-green-700 hover:-translate-y-0.5 transition-all active:scale-95 whitespace-nowrap"
              >
                <FiPlusCircle size={16} />
                <span>Generate New Invoice</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Invoice</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Patient</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Paid</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Balance</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-14 text-center text-slate-400 font-bold">No records found.</td>
                  </tr>
                ) : filteredPayments.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => setQrModal(p.invoice_no)}
                          className="px-1.5 py-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:scale-105 hover:bg-syana-blue-50 hover:border-syana-blue-200 transition-all flex items-center space-x-2"
                          title="View QR Code"
                        >
                          <QRCodeSVG value={p.invoice_no} size={20} />
                        </button>
                        <span className="font-black text-xs text-slate-700">{p.invoice_no}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 rounded bg-syana-blue-50 text-syana-blue-600 flex items-center justify-center flex-shrink-0"><FiUser size={10} /></div>
                          <span className="font-bold text-xs text-slate-700">{p.patient_name || '—'}</span>
                        </div>
                        {p.dispensing_details && (() => {
                           let disp;
                           try {
                             disp = typeof p.dispensing_details === 'string' ? JSON.parse(p.dispensing_details) : p.dispensing_details;
                           } catch(e) { return null; }
                           if (!disp) return null;
                           return (
                             <div className="mt-1 pl-8 flex items-center gap-2">
                                <span className="text-[9px] font-black text-syana-blue-600 border border-syana-blue-100 bg-syana-blue-50 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                   {disp.frame_brand}
                                </span>
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[100px]">
                                   {disp.lens_type?.replace(/_/g, ' ')}
                                </span>
                             </div>
                           )
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-black text-slate-900">Rs. {parseFloat(p.total_amount || p.amount || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 font-black text-green-600">Rs. {parseFloat(p.paid_amount || p.amount || 0).toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`font-black ${parseFloat(p.balance || 0) > 0 ? 'text-orange-500' : 'text-slate-400'}`}>
                        Rs. {parseFloat(p.balance || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <span className={`w-fit px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          p.status === 'paid' ? 'bg-green-100 text-green-700' :
                          p.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>{p.status}</span>
                        {p.delivery_date && (
                          <div className="flex items-center space-x-1.5 text-slate-400">
                             <FiCalendar size={10} />
                             <span className="text-[9px] font-bold uppercase tracking-tighter">{p.delivery_date}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {/* VIEW HISTORY */}
                        <button
                          onClick={() => {
                            setHistoryModal(p);
                            fetchPaymentHistory(p.id);
                          }}
                          className="p-2 bg-syana-blue-50 text-syana-blue-600 rounded-lg hover:bg-syana-blue-600 hover:text-white transition-all transform hover:scale-110 shadow-sm"
                          title="View Payment History"
                        >
                          <FiEye size={14} />
                        </button>

                        {/* PAY BILL (TOP UP) */}
                        {parseFloat(p.balance || 0) > 0 && (
                          <button
                            onClick={() => {
                              setTopUpModal(p);
                              setTopUpAmount(p.balance);
                            }}
                            className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-600 hover:text-white transition-all transform hover:scale-110 shadow-sm"
                            title="Pay Bill"
                          >
                            <FiPlus size={14} />
                          </button>
                        )}

                        {/* EDIT DETAILS */}
                        <button
                          onClick={() => {
                            setEditModal(p);
                            setEditNotes(p.notes || '');
                            setEditDeliveryDate(p.delivery_date || '');
                          }}
                          className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-700 hover:text-white transition-all transform hover:scale-110 shadow-sm"
                          title="Edit Details"
                        >
                          <FiEdit2 size={14} />
                        </button>

                        {/* PRINT */}
                        <button
                          onClick={() => {
                            setPrintData({
                              invoice_no: p.invoice_no,
                              date: p.payment_date,
                              patient_name: p.patient_name,
                              patient_contact: p.patient_contact,
                              total: parseFloat(p.total_amount),
                              paid: parseFloat(p.paid_amount),
                              balance: parseFloat(p.balance),
                              items: JSON.parse(p.items || '[]'),
                              delivery_date: p.delivery_date,
                              next_visit: p.next_visit
                            });
                            // Trigger print after state update
                            setTimeout(() => window.print(), 100);
                          }}
                          className="p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-500 hover:text-white transition-all transform hover:scale-110 shadow-sm"
                          title="Print Receipt"
                        >
                          <FiPrinter size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Generate Invoice Popup Modal ── */}
      {invoiceModal && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setInvoiceModal(false)}
        >
            <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-xl overflow-hidden"
            style={{ maxHeight: '95vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-green-50 to-emerald-50 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shadow-sm">
                  <FiDollarSign className="text-lg" />
                </div>
                <div>
                  <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Billing Center</h3>
                  <h4 className="font-bold text-sm text-slate-900">Generate Invoice</h4>
                </div>
              </div>
              <button onClick={() => setInvoiceModal(false)} className="text-slate-400 hover:text-slate-800 transition-all p-2 rounded-full hover:bg-slate-200">
                <FiX className="text-lg" />
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="p-6 space-y-6">
                {prefilledPatient && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-lg text-[10px] font-bold uppercase tracking-widest flex items-center space-x-2 border border-green-100">
                    <FiCheckCircle className="text-sm flex-shrink-0" />
                    <span>New patient linked</span>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Customer Information</label>
                    <button 
                      type="button"
                      onClick={() => setShowManualPatient(!showManualPatient)}
                      className={`text-[9px] font-black px-2 py-1 rounded border transition-all ${showManualPatient ? 'bg-syana-blue-600 text-white border-syana-blue-600' : 'bg-white text-slate-400 border-slate-200'}`}
                    >
                      {showManualPatient ? '✓ WALK-IN MODE' : '+ WALK-IN PATIENT'}
                    </button>
                  </div>

                  {showManualPatient ? (
                    <div className="space-y-1 animate-in slide-in-from-top-2">
                       <input 
                         type="text" 
                         value={customPatientName} 
                         onChange={e => setCustomPatientName(e.target.value)}
                         placeholder="Enter Customer Name Manually"
                         className="w-full px-4 py-3 bg-white border-2 border-syana-blue-400 rounded-lg text-sm font-bold shadow-sm focus:ring-4 focus:ring-syana-blue-50"
                       />
                    </div>
                  ) : (
                    <select
                      value={selectedPatientId}
                      onChange={e => handlePatientSelect(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm transition-all"
                    >
                      <option value="" disabled>Select User</option>
                      {prefilledPatient && !patients.find(p => p.id === prefilledPatient.id) && (
                        <option value={prefilledPatient.id}>{prefilledPatient.name}</option>
                      )}
                      {patients.map(p => <option key={p.id} value={p.id}>{p.name} — {p.contact}</option>)}
                    </select>
                  )}
                </div>


                {selectedPatientId && (() => {
                  const pat = patients.find(p => p.id.toString() === selectedPatientId.toString()) || prefilledPatient;
                  if (!pat || !pat.dispensing_details) return null;
                  let disp = pat.dispensing_details;
                  if (typeof disp === 'string') { try { disp = JSON.parse(disp); } catch(e) { return null; } }
                  
                  return (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                         <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Selected Job Specs</h5>
                         <span className="text-[9px] font-bold text-slate-400"># {pat.reg_no}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-1">
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Frame</p>
                            <p className="text-xs font-black text-slate-800 leading-tight">{disp.frame_brand}</p>
                            <p className="text-[10px] font-bold text-slate-500 capitalize">{disp.frame_demographic} | {disp.frame_material}</p>
                            {disp.frame_material === 'metal' && disp.frame_metal_type && (
                               <p className="text-[9px] font-black text-syana-blue-600 uppercase tracking-tighter mt-1">Style: {disp.frame_metal_type.replace(/_/g, ' ')}</p>
                            )}
                         </div>
                         <div className="space-y-1 border-l border-slate-200 pl-4">
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Lens</p>
                            <p className="text-xs font-black text-slate-800 leading-tight capitalize">{disp.lens_type?.replace(/_/g, ' ')}</p>
                            {(disp.cr39_bifocal_type || disp.cr39_bifocal_shape) && (
                               <p className="text-[9px] font-bold text-teal-600 uppercase tracking-tighter mt-0.5">
                                  {disp.cr39_bifocal_type} • {disp.cr39_bifocal_shape?.replace(/_/g, ' ')}
                               </p>
                            )}
                         </div>
                      </div>
                    </div>
                  );
                })()}

                {getPricingContext()}

                 {/* JOB SPECIFICATIONS (Database + Manual) */}
                <div className="space-y-3">
                   <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Job Specifications</h4>
                      <button 
                        type="button"
                        onClick={() => setShowManualSpecs(!showManualSpecs)}
                        className={`text-[9px] font-black px-2 py-1 rounded border transition-all ${showManualSpecs ? 'bg-syana-blue-600 text-white border-syana-blue-600' : 'bg-white text-slate-400 border-slate-200 hover:border-syana-blue-400 hover:text-syana-blue-600'}`}
                      >
                        {showManualSpecs ? '✓ SPECIFICATIONS ACTIVE' : '+ ADD JOB SPECS'}
                      </button>
                   </div>

                   {showManualSpecs && (
                      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-6 animate-in slide-in-from-top-4 duration-500 shadow-inner">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Frame Selection */}
                            <div className="space-y-4">
                              <div className="space-y-2 group">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-all">Frame Selection</label>
                                  <button 
                                    type="button"
                                    onClick={toggleManualFrameSpec}
                                    className={`text-[8px] font-black px-2 py-0.5 rounded border transition-all ${showManualFrameSpec ? 'bg-syana-blue-600 text-white border-syana-blue-600' : 'bg-white text-slate-400 border-slate-200 hover:border-syana-blue-400'}`}
                                  >
                                    {showManualFrameSpec ? '✓ MANUAL' : '+ MANUAL'}
                                  </button>
                                </div>
                                
                                {showManualFrameSpec ? (
                                  <input 
                                    type="text" 
                                    value={mFrame} 
                                    onChange={e => setMFrame(e.target.value)} 
                                    className="w-full px-4 py-2.5 bg-white border-2 border-syana-blue-400 rounded-xl text-sm font-bold shadow-sm focus:ring-4 focus:ring-syana-blue-50" 
                                    placeholder="Type Frame Name..." 
                                  />
                                ) : (
                                  <select 
                                    value={mFrame} 
                                    onChange={e => setMFrame(e.target.value)} 
                                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 outline-none cursor-pointer"
                                  >
                                    <option value="">Select frame from stock...</option>
                                    <option value="patient_own">Patient's Own Frame</option>
                                    {inventory.filter(i => i.category === 'frame').map(f => (
                                      <option key={f.id} value={f.item_name}>{f.item_name}</option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                   <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Demographic</label>
                                   <select 
                                     value={mDemographic} 
                                     onChange={e => setMDemographic(e.target.value)}
                                     className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:border-syana-blue-400 outline-none"
                                   >
                                      <option value="unisex">Unisex</option>
                                      <option value="gents">Gents</option>
                                      <option value="women">Women</option>
                                      <option value="children">Children</option>
                                   </select>
                                </div>
                                <div className="space-y-1">
                                   <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Material</label>
                                   <select 
                                     value={mMaterial} 
                                     onChange={e => setMMaterial(e.target.value)}
                                     className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:border-syana-blue-400 outline-none"
                                   >
                                      <option value="plastic">Plastic</option>
                                      <option value="metal">Metal</option>
                                   </select>
                                </div>
                             </div>

                             {mMaterial === 'metal' && (
                                <div className="space-y-2 group animate-in slide-in-from-top-2 duration-300">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-syana-blue-600">Metal Frame Type</label>
                                  <select 
                                    value={mMetalType} 
                                    onChange={e => setMMetalType(e.target.value)} 
                                    className="w-full px-4 py-3 bg-syana-blue-50 border border-syana-blue-200 rounded-xl text-sm font-bold text-syana-blue-900 cursor-pointer outline-none focus:ring-4 focus:ring-syana-blue-100 shadow-inner"
                                  >
                                    <option value="full_frame">Full Frame</option>
                                    <option value="half_rimless">Half Rimless</option>
                                    <option value="full_rimless">Full Rimless</option>
                                  </select>
                                </div>
                             )}
                            </div>

                            {/* Lens Selection & Sub-Options */}
                            <div className="space-y-6">
                               <div className="space-y-2 group">
                                 <div className="flex items-center justify-between">
                                   <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest group-focus-within:text-teal-600 transition-all">Lens Selection</label>
                                   <button 
                                     type="button"
                                     onClick={toggleManualLensSpec}
                                     className={`text-[8px] font-black px-2 py-0.5 rounded border transition-all ${showManualLensSpec ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-400 border-slate-200 hover:border-teal-400'}`}
                                   >
                                     {showManualLensSpec ? '✓ MANUAL' : '+ MANUAL'}
                                   </button>
                                 </div>
   
                                 {showManualLensSpec ? (
                                   <input 
                                     type="text" 
                                     value={mLens} 
                                     onChange={e => setMLens(e.target.value)} 
                                     className="w-full px-4 py-2.5 bg-white border-2 border-teal-400 rounded-xl text-sm font-bold shadow-sm focus:ring-4 focus:ring-teal-50" 
                                     placeholder="Type Lens Details..." 
                                   />
                                 ) : (
                                   <select 
                                     value={mLens} 
                                     onChange={e => setMLens(e.target.value)} 
                                     className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm focus:border-teal-400 focus:ring-4 focus:ring-teal-50 outline-none cursor-pointer"
                                   >
                                     <option value="">Select lens type...</option>
                                     {inventory.filter(i => i.category === 'lens').map(l => (
                                       <option key={l.id} value={l.item_name}>{l.item_name}</option>
                                     ))}
                                   </select>
                                 )}
                                </div>
   
                                {/* Lens Sub-Options */}
                                {mLens.toLowerCase().replace(/\s+/g, '_') === 'cr39_single_vision' && (
                                   <div className="space-y-4 animate-in slide-in-from-top-2 duration-300 bg-teal-50/40 p-5 rounded-xl border border-teal-200/50 shadow-inner">
                                      <div className="space-y-2 group">
                                         <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Single Vision Version</label>
                                         <select 
                                           value={mSvType} 
                                           onChange={e => setMSvType(e.target.value)}
                                           className="w-full px-4 py-3 bg-white border border-teal-200 rounded-xl text-sm font-bold shadow-sm focus:ring-4 focus:ring-teal-100 outline-none cursor-pointer"
                                         >
                                            <option value="minus">Minus</option>
                                            <option value="cylindrical">Cylindrical</option>
                                         </select>
                                      </div>
                                   </div>
                                )}
   
                                {mLens.toLowerCase().replace(/\s+/g, '_') === 'cr39_bifocal' && (
                                   <div className="space-y-4 animate-in slide-in-from-top-2 duration-300 bg-teal-50/40 p-5 rounded-xl border border-teal-200/50 shadow-inner">
                                      <div className="space-y-2 group">
                                         <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Bifocal Type</label>
                                         <select 
                                           value={mBifocalType} 
                                           onChange={e => setMBifocalType(e.target.value)}
                                           className="w-full px-4 py-3 bg-white border border-teal-200 rounded-xl text-sm font-bold shadow-sm focus:ring-4 focus:ring-teal-100 outline-none cursor-pointer"
                                         >
                                            <option value="normal">Normal</option>
                                            <option value="cylindrical">Cylindrical</option>
                                         </select>
                                      </div>
                                      <div className="space-y-2 group">
                                         <label className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Bifocal Shape</label>
                                         <select 
                                           value={mBifocalShape} 
                                           onChange={e => setMBifocalShape(e.target.value)}
                                           className="w-full px-4 py-3 bg-white border border-teal-200 rounded-xl text-sm font-bold shadow-sm focus:ring-4 focus:ring-teal-100 outline-none cursor-pointer"
                                         >
                                            <option value="round_top">Round Top</option>
                                            <option value="flat_top">Flat Top</option>
                                         </select>
                                      </div>
                                   </div>
                                )}
                            </div>
                        </div>
                      </div>
                   )}
                </div>

                {/* Inventory Products */}
                <div className="bg-slate-50 p-4 border border-slate-200 rounded-xl space-y-4">
                   <div className="flex items-center space-x-2 text-syana-blue-600">
                     <FiBox />
                     <h4 className="text-[10px] font-black uppercase tracking-widest">Include Inventory Products</h4>
                   </div>
                   
                   <div className="flex gap-2 items-end">
                      <div className="flex-1 space-y-1 group">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inventory Item</label>
                        <select 
                          value={inventorySelect} 
                          onChange={e => setInventorySelect(e.target.value)}
                          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-semibold outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 cursor-pointer"
                        >
                          <option value="">Choose item...</option>
                          {inventory.map(i => (
                            <option key={i.id} value={i.id}>{i.item_name} - Rs. {parseFloat(i.price).toFixed(2)} (Qty: {i.quantity})</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-20 space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Qty</label>
                        <input type="number" min="1" value={inventoryQty} onChange={e => setInventoryQty(e.target.value)} className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-bold text-center outline-none focus:border-syana-blue-400" />
                      </div>
                      <button type="button" onClick={addItemToInvoice} className="px-4 py-2.5 bg-slate-900 text-white rounded-lg font-bold text-[10px] uppercase tracking-wider hover:bg-slate-800 transition-colors">Add</button>
                   </div>
                   
                   {selectedItems.length > 0 && (
                     <div className="mt-3 space-y-2 max-h-32 overflow-y-auto no-scrollbar">
                       {selectedItems.map((itm, idx) => (
                         <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-lg border border-slate-200 shadow-sm text-sm">
                           <div>
                             <span className="font-bold text-slate-800">{itm.item_name}</span>
                             <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest block">Rs. {parseFloat(itm.price).toFixed(2)} x {itm.qty}</span>
                           </div>
                           <div className="flex items-center space-x-3">
                             <span className="font-black text-syana-blue-600">Rs. {(parseFloat(itm.price)*itm.qty).toFixed(2)}</span>
                             <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><FiTrash2 size={16} /></button>
                           </div>
                         </div>
                       ))}
                     </div>
                   )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Expected Delivery Date (Optional)</label>
                    <div className="relative">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="date"
                        value={deliveryDate}
                        onChange={e => setDeliveryDate(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-lg focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-bold shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-[#f1c40f]">Automated Next Visit (Months)</label>
                    <div className="relative">
                      <FiClock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f1c40f]" />
                      <select
                        onChange={e => {
                          const m = e.target.value;
                          if (!m) { setVolatileNextVisit(''); return; }
                          const d = new Date();
                          d.setMonth(d.getMonth() + parseInt(m));
                          setVolatileNextVisit(d.toISOString().split('T')[0]);
                        }}
                        className="w-full pl-11 pr-4 py-3 bg-white border border-[#f1c40f]/30 rounded-lg focus:border-[#f1c40f] focus:ring-4 focus:ring-[#f1c40f]/10 text-sm font-bold shadow-sm cursor-pointer"
                      >
                        <option value="">No Follow-up</option>
                        <option value="1">1 Month</option>
                        <option value="3">3 Months</option>
                        <option value="6">6 Months</option>
                        <option value="12">1 Year</option>
                        <option value="24">2 Years</option>
                      </select>
                    </div>
                  </div>
                </div>
                {volatileNextVisit && (
                   <div className="bg-[#f1c40f]/5 border border-[#f1c40f]/20 p-2 rounded-lg text-center animate-in fade-in slide-in-from-top-1">
                      <span className="text-[9px] font-black text-[#f1c40f] uppercase tracking-widest">Scheduled Date: {volatileNextVisit}</span>
                   </div>
                )}
                <p className="text-[9px] font-bold text-slate-400 tracking-wide uppercase mt-1">Delivery shows in ledger. Next visit is for the receipt only.</p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Total Bill (Rs.)</label>
                    <input
                      type="number" min="0" step="0.01" value={totalAmount} onChange={e => setTotalAmount(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-lg focus:border-syana-blue-400 focus:ring-4 text-xl font-black text-slate-900"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Paid Today (Rs.)</label>
                    <input
                      type="number" min="0" step="0.01" value={paidToday} onChange={e => setPaidToday(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:border-green-400 focus:ring-4 focus:ring-green-50 text-xl font-black shadow-sm text-green-700"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {balance !== '' && (
                  <div className={`flex items-center justify-between px-5 py-4 rounded-xl border font-bold text-sm ${
                    parseFloat(balance) > 0 ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    <span className="text-[10px] uppercase tracking-widest font-black">Balance Due</span>
                    <span className="text-xl font-black">Rs. {balance}</span>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Remarks</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-syana-blue-400 focus:ring-4 text-sm font-semibold resize-none shadow-sm placeholder:text-slate-300" placeholder="Optional notes..."/>
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center space-x-3 sticky bottom-0">
                <button type="submit" disabled={loading} className="w-full py-3.5 bg-green-600 text-white rounded-lg font-bold uppercase text-[11px] tracking-widest shadow-md shadow-green-200 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center space-x-2">
                  {loading ? <span>Saving…</span> : <><FiCheckCircle className="text-sm" /><span>Confirm & Save Bill</span></>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {topUpModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setTopUpModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex flex-col">
              <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Balance Payment</h3>
              <h4 className="font-bold text-sm text-slate-900">{topUpModal.patient_name}</h4>
              <button onClick={() => setTopUpModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"><FiX className="text-xl" /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex justify-between items-center bg-orange-50 border border-orange-200 rounded-xl px-5 py-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-orange-500">Remaining Balance</span>
                <span className="text-2xl font-black text-orange-600">Rs. {parseFloat(topUpModal.balance || 0).toFixed(2)}</span>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Amount to Pay Now (Rs.)</label>
                <input type="number" min="0.01" step="0.01" value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg focus:border-green-400 text-xl font-black shadow-sm" autoFocus />
              </div>
              
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50">
              <button onClick={handleTopUp} disabled={topUpLoading} className="w-full py-3.5 bg-green-600 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-md flex items-center justify-center space-x-2">
                {topUpLoading ? <span>Processing…</span> : <><FiCheckCircle className="text-sm" /><span>Confirm Payment</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm flex flex-col items-center text-center" onClick={e => e.stopPropagation()}>
            <QRCodeSVG value={qrModal} size={200} />
            <h4 className="font-black text-lg text-slate-900 mt-6">{qrModal}</h4>
            <button onClick={() => setQrModal(null)} className="mt-8 w-full py-4 bg-slate-900 text-white rounded-xl font-bold uppercase text-[11px] tracking-widest">Close Window</button>
          </div>
        </div>
      )}

      {/* ── Hidden Print Area (A5 format) ── */}
      <style>{`
        @media print {
          @page { size: 148mm 210mm; margin: 0; }
          body * { visibility: hidden !important; }
          #print-receipt, #print-receipt * { visibility: visible !important; }
          #print-receipt { 
            display: block !important; 
            position: absolute !important; 
            left: 0 !important; 
            top: 0 !important; 
            width: 148mm !important;
            height: 209mm !important;
            margin: 0 !important; 
            padding: 8mm !important; 
            box-sizing: border-box !important;
            background: white !important;
            color: #1a1a1a !important;
            font-family: 'Inter', system-ui, sans-serif !important;
          }
          .yellow-accent { background-color: #130ff1ff !important; }
          .dark-accent { background-color: #2c3e50 !important; color: white !important; }
          .zebra-row:nth-child(even) { background-color: #f9f9f9 !important; }
        }
      `}</style>

      {printData && (
        <div id="print-receipt" style={{ display: 'none' }} className="bg-white text-black">
          {/* Header */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-900 rounded-lg">
                <img src="/logo.png" alt="Syana" className="w-8 h-8 object-contain brightness-0 invert" />
              </div>
              <div>
                <h1 className="text-xl font-black uppercase tracking-tight text-slate-900">Siyena</h1>
                <p className="text-[7px] font-bold uppercase tracking-widest text-[#f1c40f] -mt-1">Optometrists </p>
              </div>
            </div>
            <div className="text-right">
               <h2 className="text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">Invoice</h2>
            </div>
          </div>

          {/* Yellow Bar */}
          <div className="w-full h-8 yellow-accent flex items-center justify-end px-4 mb-6">
             <div className="w-1/2 h-1 bg-white/20 rounded-full"></div>
          </div>

          {/* Info Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="space-y-1">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Invoice to:</h3>
              <div className="text-sm font-black text-slate-900 uppercase">{printData.patient_name}</div>
              <div className="text-[9px] font-bold text-slate-600">
                Registration: <span className="text-slate-900">{printData.patient_reg_no}</span><br />
                Contact: <span className="text-slate-900">{printData.patient_contact}</span>
              </div>
            </div>
            
            <div className="flex justify-end">
              <div className="space-y-1.5 w-48 text-[9px] font-bold">
                <div className="flex justify-between">
                  <span className="text-slate-400 uppercase tracking-widest">Invoice#</span>
                  <span className="text-slate-900 uppercase">{printData.invoice_no}</span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-1">
                  <span className="text-slate-400 uppercase tracking-widest">Date</span>
                  <span className="text-slate-900">{printData.date}</span>
                </div>
                {printData.delivery_date && (
                   <div className="flex justify-between pt-1 text-slate-900 bg-[#f1c40f]/10 px-2 py-1 rounded">
                     <span className="uppercase tracking-widest">Delivery</span>
                     <span className="font-black">{printData.delivery_date}</span>
                   </div>
                )}
                {printData.next_visit && (
                   <div className="flex justify-between pt-1 text-slate-900 bg-green-50 px-2 py-1 rounded mt-1 border border-green-100">
                     <span className="uppercase tracking-widest">Next Visit</span>
                     <span className="font-black underline decoration-[#f1c40f] underline-offset-2">{printData.next_visit}</span>
                   </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mb-8">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="dark-accent text-[8px] uppercase tracking-widest font-black">
                  <th className="py-2.5 px-3 rounded-l-md border-0">SL.</th>
                  <th className="py-2.5 px-3 border-0">Item Description</th>
                  <th className="py-2.5 px-3 border-0 whitespace-nowrap">Price</th>
                  <th className="py-2.5 px-3 border-0 text-center">Qty.</th>
                  <th className="py-2.5 px-3 rounded-r-md border-0 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="text-[10px]">
                {/* 1. Add Frame if exists */}
                {printData.dispensing_details?.frame_brand && (
                  <tr className="zebra-row font-bold bordr-b border-slate-50">
                    <td className="py-3 px-3 text-slate-400 italic">01</td>
                    <td className="py-3 px-3">
                       <div className="font-black text-slate-800">FRAME: {printData.dispensing_details.frame_brand}</div>
                       <div className="text-[8px] text-slate-400 uppercase tracking-wider">{printData.dispensing_details.frame_material} | {printData.dispensing_details.frame_demographic}</div>
                    </td>
                    <td className="py-3 px-3 text-slate-400">—</td>
                    <td className="py-3 px-3 text-center">1</td>
                    <td className="py-3 px-3 text-right font-black text-slate-900">—</td>
                  </tr>
                )}
                
                {/* 2. Add Lens if exists */}
                {printData.dispensing_details?.lens_type && printData.dispensing_details.lens_type !== 'none' && (
                  <tr className="zebra-row font-bold bordr-b border-slate-50">
                    <td className="py-3 px-3 text-slate-400 italic">02</td>
                    <td className="py-3 px-3">
                       <div className="font-black text-slate-800">LENS: {printData.dispensing_details.lens_type.replace(/_/g, ' ')}</div>
                       {printData.dispensing_details.cr39_bifocal_type && (
                         <div className="text-[8px] text-slate-400 uppercase tracking-wider">{printData.dispensing_details.cr39_bifocal_type} • {printData.dispensing_details.cr39_bifocal_shape}</div>
                       )}
                    </td>
                    <td className="py-3 px-3 text-slate-400">—</td>
                    <td className="py-3 px-3 text-center">1</td>
                    <td className="py-3 px-3 text-right font-black text-slate-900">—</td>
                  </tr>
                )}

                {/* 3. Itemized Inventory Products */}
                {(printData.items || []).map((itm, idx) => {
                  const sl = (printData.dispensing_details?.frame_brand ? 1 : 0) + (printData.dispensing_details?.lens_type && printData.dispensing_details.lens_type !== 'none' ? 1 : 0) + idx + 1;
                  return (
                    <tr key={idx} className="zebra-row font-bold border-b border-slate-50">
                      <td className="py-3 px-3 text-slate-400 italic">{sl.toString().padStart(2, '0')}</td>
                      <td className="py-3 px-3">
                        <div className="font-black text-slate-800 uppercase leading-none">{itm.item_name}</div>
                        <div className="text-[7px] text-slate-400 uppercase tracking-widest mt-0.5">{itm.category || 'General Product'}</div>
                      </td>
                      <td className="py-3 px-3 font-bold text-slate-600">Rs. {parseFloat(itm.price).toFixed(2)}</td>
                      <td className="py-3 px-3 text-center text-slate-600">{itm.qty}</td>
                      <td className="py-3 px-3 text-right font-black text-slate-900">Rs. {(parseFloat(itm.price) * itm.qty).toFixed(2)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Summary & Terms */}
          <div className="flex justify-between items-start mb-8">
            <div className="w-1/2 space-y-4">
              <div>
                <h4 className="text-[9px] font-black uppercase tracking-widest text-slate-900 mb-1.5 underline underline-offset-4 decoration-[#f1c40f]">Thank you for your business</h4>
                <p className="text-[8px] text-slate-500 font-bold leading-relaxed">
                  We appreciate your trust in Syana Eyecare. For any visual concerns or adjustments, please visit us within 7 days of collection.
                </p>
              </div>
              {/* <div className="space-y-1">
                <h5 className="text-[7px] font-black uppercase tracking-widest text-slate-400">Terms & Conditions</h5>
                <ul className="text-[7px] text-slate-400 font-bold space-y-0.5 list-disc list-inside">
                  <li>Lenses are custom-made and non-refundable.</li>
                  <li>Balance must be settled before collection.</li>
                  <li>Standard 6-month warranty on manufacturing defects.</li>
                </ul>
              </div> */}
            </div>

            <div className="w-56 space-y-1">
              <div className="flex justify-between items-center px-3 py-1 text-[9px] font-black text-slate-400">
                <span>SUB TOTAL:</span>
                <span className="text-slate-900">Rs. {printData.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-1 text-[9px] font-black text-slate-400">
                <span>TAX (0.00%):</span>
                <span className="text-slate-900">Rs. 0.00</span>
              </div>
              <div className="flex justify-between items-center px-4 py-3 yellow-accent rounded-md mt-2">
                <span className="text-xs font-black uppercase tracking-widest">Total:</span>
                <span className="text-lg font-black tracking-tight">Rs. {printData.total.toFixed(2)}</span>
              </div>
              {parseFloat(printData.balance) > 0 && (
                 <div className="flex justify-between items-center px-3 py-1 text-[9px] font-black text-orange-600 bg-orange-50 rounded mt-1 border border-orange-100">
                   <span>BALANCE DUE:</span>
                   <span>Rs. {parseFloat(printData.balance).toFixed(2)}</span>
                 </div>
              )}
            </div>
          </div>

          {/* Footer Info & Signature */}
          <div className="mt-auto">
            <div className="flex justify-between items-end mb-6">
               <div className="space-y-2">
                  <div className="space-y-0.5">
                    <h5 className="text-[7px] font-black uppercase tracking-widest text-slate-400">Payment Info:</h5>
                    <div className="text-[7px] font-bold text-slate-600">
                      Bank: HNB Bank Colombo 07<br />
                      Acc: 1234 5678 9012 (Siyena Optometrists)
                    </div>
                  </div> 
                  <QRCodeSVG value={printData.invoice_no} size={70} />
               </div>
               <div className="text-center w-48 border-t-2 border-slate-100 pt-2">
                  <div className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-900">Authorised Sign</div>
                  <div className="text-[6px] font-bold text-slate-400 tracking-widest mt-1">SYANA EYECARE SOLUTIONS</div>
               </div>
            </div>

            <div className="border-t border-slate-100 pt-3 flex justify-center space-x-6 text-[7px] font-black text-slate-300 uppercase tracking-[0.2em]">
               <span>+94 11 75 75 100</span>
               <span>No. 06, Ward Place, Colombo 07</span>
               <span>www.syanaeyecare.lk</span>
            </div>
          </div>
        </div>
      )}

      
        {/* Payment History / Individual Installments Modal */}
        {historyModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                   <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-2xl bg-syana-blue-600 flex items-center justify-center text-white shadow-lg shadow-syana-blue-200">
                         <FiInbox size={24} />
                      </div>
                      <div>
                         <h3 className="text-xl font-black text-slate-800 tracking-tight">Invoice Details</h3>
                         <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{historyModal.invoice_no}</p>
                      </div>
                   </div>
                   <button onClick={() => setHistoryModal(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-all"><FiX size={20} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-hide">
                   {/* Summary Cards */}
                   <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                         <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Bill</p>
                         <p className="text-lg font-black text-slate-900 leading-none">Rs. {parseFloat(historyModal.total_amount).toFixed(2)}</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-green-50 border border-green-100">
                         <p className="text-[9px] font-bold text-green-600 uppercase tracking-widest mb-1">Paid Status</p>
                         <p className="text-lg font-black text-green-700 leading-none">Rs. {parseFloat(historyModal.paid_amount).toFixed(2)}</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-orange-50 border border-orange-100">
                         <p className="text-[9px] font-bold text-orange-600 uppercase tracking-widest mb-1">Remaining</p>
                         <p className="text-lg font-black text-orange-700 leading-none">Rs. {parseFloat(historyModal.balance).toFixed(2)}</p>
                      </div>
                   </div>

                   {/* Payment History Section */}
                   <div className="space-y-4">
                      <div className="flex items-center space-x-3 pb-2 border-b border-slate-100">
                         <FiClock className="text-syana-blue-600" />
                         <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">One by One Payment History</h4>
                      </div>
                      
                      {loadingHistory ? (
                         <div className="py-12 flex flex-col items-center justify-center space-y-3">
                            <div className="w-8 h-8 border-4 border-syana-blue-100 border-t-syana-blue-600 rounded-full animate-spin"></div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Fetching Payments...</p>
                         </div>
                      ) : paymentHistory.length > 0 ? (
                         <div className="relative space-y-4 before:absolute before:left-6 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                            {paymentHistory.map((log, idx) => (
                               <div key={log.id} className="relative flex items-center space-x-6 animate-in slide-in-from-left-4 duration-500" style={{ animationDelay: `${idx * 100}ms` }}>
                                  <div className={`z-10 w-12 h-12 rounded-full border-4 border-white shadow-md flex items-center justify-center text-white ${idx === 0 ? 'bg-green-500' : 'bg-slate-400'}`}>
                                     <FiCheck size={18} />
                                  </div>
                                  <div className="flex-1 bg-slate-50/50 border border-slate-100 p-4 rounded-2xl flex justify-between items-center group hover:border-syana-blue-200 hover:bg-white transition-all">
                                     <div>
                                        <p className="text-sm font-black text-slate-800">Rs. {parseFloat(log.amount_paid).toFixed(2)}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{log.note}</p>
                                     </div>
                                     <div className="text-right">
                                        <p className="text-[10px] font-bold text-slate-600">{new Date(log.payment_date).toLocaleDateString()}</p>
                                        <p className="text-[9px] font-bold text-slate-400">{new Date(log.payment_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                     </div>
                                  </div>
                               </div>
                            ))}
                         </div>
                      ) : (
                         <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No detailed payment records found</p>
                         </div>
                      )}
                   </div>
                </div>

                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center px-8">
                   <div className="flex items-center space-x-2">
                       <FiUser className="text-slate-400" />
                       <span className="text-[10px] font-bold text-slate-600 uppercase">Patient: {historyModal.patient_name}</span>
                   </div>
                   <button 
                     onClick={() => window.print()}
                     className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center space-x-2"
                   >
                     <FiPrinter />
                     <span>Print Statement</span>
                   </button>
                </div>
             </div>
          </div>
        )}

        {/* Edit Details Modal */}
        {editModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-300">
                <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                   <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-lg">
                         <FiEdit2 size={20} />
                      </div>
                      <div>
                         <h3 className="text-lg font-black text-slate-800 tracking-tight">Edit Details</h3>
                         <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{editModal.invoice_no}</p>
                      </div>
                   </div>
                   <button onClick={() => setEditModal(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-all"><FiX size={20} /></button>
                </div>

                <form onSubmit={handleUpdateDetails} className="p-8 space-y-6">
                   <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Expected Delivery Date</label>
                      <div className="relative">
                         <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                         <input
                           type="date"
                           value={editDeliveryDate}
                           onChange={e => setEditDeliveryDate(e.target.value)}
                           className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-bold transition-all"
                         />
                      </div>
                   </div>

                   <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Administrative Notes</label>
                      <textarea
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        placeholder="Add additional instructions..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 text-sm font-bold min-h-[120px] transition-all"
                      ></textarea>
                   </div>

                   <div className="pt-4 flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setEditModal(null)}
                        className="flex-1 px-6 py-3 border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={editLoading}
                        className="flex-[2] px-6 py-3 bg-syana-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-syana-blue-700 transition-all shadow-lg shadow-syana-blue-100 flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        {editLoading ? (
                           <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        ) : (
                           <>
                              <FiCheckCircle />
                              <span>Save Changes</span>
                           </>
                        )}
                      </button>
                   </div>
                </form>
             </div>
          </div>
        )}

      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} onScan={handleQRScanResult} />}
    </div>
  );
};

export default Payments;
